import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    totalProducts: 0,
    lowStockProducts: 0,
    totalRevenue: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { user } = useContext(AuthContext);
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }
        
        // Fetch orders
        const ordersRes = await axios.get('/api/orders/all', {
          headers: {
            'x-auth-token': token
          }
        });
        
        // Fetch products
        const productsRes = await axios.get('/api/products', {
          headers: {
            'x-auth-token': token
          }
        });
        
        // Calculate stats
        const orders = ordersRes.data;
        const products = productsRes.data;
        
        const pendingOrders = orders.filter(order => order.status === 'pending').length;
        const lowStockProducts = products.filter(product => product.stock < 5).length;
        const totalRevenue = orders
          .filter(order => order.isPaid)
          .reduce((sum, order) => sum + order.totalAmount, 0);
        
        setStats({
          totalOrders: orders.length,
          pendingOrders,
          totalProducts: products.length,
          lowStockProducts,
          totalRevenue
        });
        
        // Get recent orders (last 5)
        setRecentOrders(orders.slice(0, 5));
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="neumorph p-8 rounded-full">
          <svg className="animate-spin h-8 w-8 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-center py-8">
        <div className="neumorph p-6 rounded-lg inline-block">
          <h3 className="text-xl text-red-600 mb-2">Error</h3>
          <p className="text-gray-700">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="neumorph-btn mt-4 px-4 py-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <div>
          <span className="text-gray-600 mr-2">Welcome,</span>
          <span className="font-semibold">{user?.name}</span>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="neumorph p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-gray-600">Total Orders</h3>
            <div className="neumorph p-2 rounded-full bg-blue-100 text-blue-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
          </div>
          <div className="text-3xl font-bold">{stats.totalOrders}</div>
          <Link to="/admin/orders" className="text-blue-600 text-sm hover:underline mt-2 inline-block">
            View all orders
          </Link>
        </div>
        
        <div className="neumorph p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-gray-600">Pending Orders</h3>
            <div className="neumorph p-2 rounded-full bg-yellow-100 text-yellow-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="text-3xl font-bold">{stats.pendingOrders}</div>
          <Link to="/admin/orders?status=pending" className="text-blue-600 text-sm hover:underline mt-2 inline-block">
            View pending orders
          </Link>
        </div>
        
        <div className="neumorph p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-gray-600">Total Products</h3>
            <div className="neumorph p-2 rounded-full bg-green-100 text-green-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </div>
          </div>
          <div className="text-3xl font-bold">{stats.totalProducts}</div>
          <Link to="/admin/products" className="text-blue-600 text-sm hover:underline mt-2 inline-block">
            Manage products
          </Link>
        </div>
        
        <div className="neumorph p-6 rounded-lg">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-gray-600">Total Revenue</h3>
            <div className="neumorph p-2 rounded-full bg-purple-100 text-purple-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="text-3xl font-bold">${stats.totalRevenue.toFixed(2)}</div>
          <div className="text-gray-500 text-sm mt-2">
            From paid orders
          </div>
        </div>
      </div>
      
      {/* Alert for Low Stock */}
      {stats.lowStockProducts > 0 && (
        <div className="neumorph p-4 rounded-lg mb-8 bg-red-50">
          <div className="flex items-center">
            <svg className="h-6 w-6 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-red-600 font-medium">
              {stats.lowStockProducts} products have low stock (less than 5 items)
            </span>
          </div>
          <Link to="/admin/products?stock=low" className="text-red-600 text-sm hover:underline ml-8 mt-1 inline-block">
            View low stock products
          </Link>
        </div>
      )}
      
      {/* Recent Orders */}
      <div className="neumorph p-6 rounded-lg mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Recent Orders</h2>
          <Link to="/admin/orders" className="text-blue-600 hover:underline">
            View All
          </Link>
        </div>
        
        {recentOrders.length === 0 ? (
          <p className="text-gray-600 text-center py-4">No orders yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-semibold">Order ID</th>
                  <th className="pb-3 font-semibold">Customer</th>
                  <th className="pb-3 font-semibold">Date</th>
                  <th className="pb-3 font-semibold">Amount</th>
                  <th className="pb-3 font-semibold">Status</th>
                  <th className="pb-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map(order => (
                  <tr key={order._id} className="border-b last:border-b-0">
                    <td className="py-4 text-sm">{order._id.substring(0, 8)}...</td>
                    <td className="py-4 text-sm">{order.user.name}</td>
                    <td className="py-4 text-sm">{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td className="py-4 text-sm">${order.totalAmount.toFixed(2)}</td>
                    <td className="py-4 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(order.status)}`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                    </td>
                    <td className="py-4 text-sm">
                      <Link 
                        to={`/admin/orders/${order._id}`} 
                        className="neumorph-btn px-3 py-1 text-sm"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Quick Actions */}
      <div className="neumorph p-6 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Link to="/admin/products/new" className="neumorph p-4 rounded-lg hover:shadow-neumorph-inset transition-all duration-300">
            <div className="flex items-center">
              <div className="neumorph p-2 rounded-full bg-green-100 text-green-600 mr-3">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <span className="font-medium">Add New Product</span>
            </div>
          </Link>
          
          <Link to="/admin/orders?status=pending" className="neumorph p-4 rounded-lg hover:shadow-neumorph-inset transition-all duration-300">
            <div className="flex items-center">
              <div className="neumorph p-2 rounded-full bg-yellow-100 text-yellow-600 mr-3">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <span className="font-medium">Process Pending Orders</span>
            </div>
          </Link>
          
          <Link to="/admin/products?stock=low" className="neumorph p-4 rounded-lg hover:shadow-neumorph-inset transition-all duration-300">
            <div className="flex items-center">
              <div className="neumorph p-2 rounded-full bg-red-100 text-red-600 mr-3">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <span className="font-medium">Update Low Stock</span>
            </div>
          </Link>
          
          <Link to="/admin/access-codes" className="neumorph p-4 rounded-lg hover:shadow-neumorph-inset transition-all duration-300">
            <div className="flex items-center">
              <div className="neumorph p-2 rounded-full bg-purple-100 text-purple-600 mr-3">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <span className="font-medium">Manage Access Codes</span>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
